import {
  Component,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  output,
  ViewChild,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  QaiBadgeKind,
  KebabMenuItem,
  BadgeCell,
  SortDirection,
  QaiFieldComponent,
  QaiInputComponent,
  QaiIconComponent,
  QaiSlotDirective,
  QaiSearchableSelectComponent,
  QaiSearchableSelectOption,
} from '@qai/ui';
import { finalize, from, map, switchMap, take, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import {
  ISimulation,
  ISimulationGeneration,
  TableRowSimulationNameObject,
} from 'src/app/interfaces/self-serve/responses/isimulation';
import { SimulationGenerationStatus } from 'src/app/utils/enums/self-serve/simulation-generation-status.enum';
import {
  CallTypeLabelMap,
  SimulationConstants,
  SimulationMenuAction,
  SimulationMenuItem,
} from 'src/app/utils/constants/self-serve/simulation.constants';
import { AppConstants } from 'src/app/app-constants';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { filterByName } from 'src/app/utils/search.util';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import { PaginationControllerService } from 'src/app/services/self-serve/pagination-controller.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { localeLowerCase } from '../../../../../utils/locale-case.util';
import { EditSimulationTagsModalComponent } from '../modals/edit-simulation-tags-modal/edit-simulation-tags-modal.component';
import { LocaleService } from 'src/app/services/locale.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';

const SIMULATION_TABLE_HEADERS = [
  'Name',
  'Product',
  'Date last modified',
  'Status',
  'Simulation type',
];

const EXTENDED_SIMULATION_TABLE_HEADERS = [
  'Name',
  'Product',
  'Date last modified',
  'Status',
  'Used in',
  'Simulation type',
];

// Refinement runs on an already-generated simulation, so for now every
// refinement stage — in progress or finished — falls back to the normal
// Call.Status badge instead of overriding it with "Processing"/"Failed".
const REFINEMENT_STATUSES: string[] = [
  SimulationGenerationStatus.RefinementRequested,
  SimulationGenerationStatus.RefinementChangePlan,
  SimulationGenerationStatus.RefinementSectionRewrite,
  SimulationGenerationStatus.RefinementCompleted,
  SimulationGenerationStatus.RefinementFailed,
];

@Component({
  selector: 'all-simulations-table',
  standalone: true,
  templateUrl: './all-simulations-table.component.html',
  styleUrls: ['./all-simulations-table.component.scss'],
  imports: [CommonModule, QaiTableComponent, QaiLoadingComponent, QaiSearchableSelectComponent, QaiFieldComponent, QaiInputComponent, QaiIconComponent, QaiSlotDirective, TranslocoModule, EditSimulationTagsModalComponent],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AllSimulationsTableComponent implements OnInit {
  tableData: TableRow[] = [];
  headers: string[] = [];
  isLoading = true;
  initialSortColumn: string = EXTENDED_SIMULATION_TABLE_HEADERS[2] || SIMULATION_TABLE_HEADERS[2];
  initialSortDirection: SortDirection = 'desc';
  options: { label: string; value: string }[] = [];
  isTooltipLoading = false;
  private simulations: ISimulation[] = [];
  isEditTagsModalOpen = false;
  editTagsSimulation: ISimulation | undefined;
  statusOptions: QaiSearchableSelectOption[] = [];
  selectedStatusValues: string[] = [];
  visibleOptionsSelectionMode = {
    enable: true,
    selectAllOption:   { value: 'selectAll',   label: '' },
    deselectAllOption: { value: 'deselectAll', label: '' },
  };

  othersTextFormatter = (count: number): string =>
    count === 1
      ? this.translocoService.translate('simulationBuilder.OTHERS_SINGULAR')
      : this.translocoService.translate('simulationBuilder.OTHERS_PLURAL', { count });

  @ViewChild(QaiTableComponent)
  qaiTableRef: QaiTableComponent | null = null;

  @Input() selectedAccount: { label: string; value: string } | undefined;
  @Input() selectedAccountUuid: string = '';
  @Input() refreshKey: number = 0;
  @Input() selectedAccountHasAuthor: boolean = false;
  @Input() selectedAccountHasAssignmentManager: boolean = false;

  @Output() deleteSimulation = new EventEmitter<{
    id: string | number;
    rowData: TableRow;
    moduleUuid?: string;
  }>();
  @Output() duplicateSimulation = new EventEmitter<{
    id: string;
    name: string;
    moduleUuid?: string;
  }>();
  @Output() archiveSimulation = new EventEmitter<{
    id: string;
    moduleUuid?: string;
  }>();
  openGenerationModal = output<{
    callUuid: string;
    moduleUuid?: string;
    name: string;
    status: string;
  }>();

  searchName = signal('');

  currentPage: number = 1;

  private readonly featureFlagsService = inject(FeatureFlagsService);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private assignmentsService: AssignmentsService,
    private cdRef: ChangeDetectorRef,
    private PaginationControllerService: PaginationControllerService,
    private clipboard: Clipboard,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService,
    private localeService: LocaleService
  ) { }

  ngOnInit(): void {
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      this.initializeFilterOptions();
      this.updateHeaders();
      if (this.simulations.length > 0) {
        this.applyFilters(false);
      }
    });
    this.initializePagination();
  }

  private initializePagination(): void {
    this.currentPage = this.PaginationControllerService.getSimulationsCurrentPage();
  }

  private initializeFilterOptions(): void {
    // SAFE: fixed internal constants used as filter option values
    this.statusOptions = [
      { label: this.translocoService.translate('simulationBuilder.STATUS_TESTING'),   value: SimulationConstants.TESTING.toLowerCase()   },
      { label: this.translocoService.translate('simulationBuilder.STATUS_DRAFT'),     value: SimulationConstants.DRAFT.toLowerCase()     },
      { label: this.translocoService.translate('simulationBuilder.STATUS_PUBLISHED'), value: SimulationConstants.PUBLISHED.toLowerCase() },
      { label: this.translocoService.translate('simulationBuilder.STATUS_ARCHIVED'),  value: SimulationConstants.ARCHIVED.toLowerCase()  },
    ];
    this.visibleOptionsSelectionMode = {
      enable: true,
      selectAllOption:   { value: 'selectAll',   label: this.translocoService.translate('simulationBuilder.SELECT_ALL')   },
      deselectAllOption: { value: 'deselectAll', label: this.translocoService.translate('simulationBuilder.DESELECT_ALL') },
    };
  }

  private updateHeaders(): void {
    const colName = this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_NAME');
    const colProduct = this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_PRODUCT');
    const colDateModified = this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_DATE_MODIFIED');
    const colStatus = this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_STATUS');
    const colUsedIn = this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_USED_IN');
    const colType = this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_TYPE');

    this.headers = (this.selectedAccountHasAuthor && this.selectedAccountHasAssignmentManager)
      ? [colName, colProduct, colDateModified, colStatus, colUsedIn, colType]
      : [colName, colProduct, colDateModified, colStatus, colType];
    this.initialSortColumn = this.headers[2];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      this.loadSimulations();
    }
    if (changes['refreshKey']) {
      this.loadSimulations();
    }
    if (
      changes['selectedAccountHasAuthor'] ||
      changes['selectedAccountHasAssignmentManager']
    ) {
      this.updateHeaders();
    }
  }

  loadSimulations() {
    const accountUuid = this.selectedAccount?.value || this.selectedAccountUuid;

    if (!accountUuid) {
      console.error('No valid account UUID found to load simulations.');
      this.isLoading = false;
      return;
    }

    this.authorService
      .findAllSimulations(accountUuid)
      .pipe(
        switchMap((simulations) => this.hideUnpublishedKnowledgeChecksWhenFlagOff(simulations)),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (simulations) => {
          this.simulations = simulations;
          const defaultStatuses = this.statusOptions
            .map(o => o.value as string)
            // SAFE: fixed internal constant comparison
            .filter(v => v !== SimulationConstants.ARCHIVED.toLowerCase());
          this.selectedStatusValues = SelfServeLocalStorageUtil.getSimulationStatusFilters(defaultStatuses);
          this.applyFilters(false);
        },
        error: (err: any) => {
          console.error('Error loading simulations:', err);
          this.isLoading = false;
        },
      });
  }

  private hideUnpublishedKnowledgeChecksWhenFlagOff(simulations: ISimulation[]) {
    return from(this.featureFlagsService.isFlagEnabled(AppConstants.SELF_AUTHORING_KNOWLEDGE_CHECK)).pipe(
      map((isKnowledgeCheckEnabled) =>
        isKnowledgeCheckEnabled
          ? simulations
          : (simulations ?? []).filter(
              (simulation) =>
                // SAFE: internal call-type and status enum comparison
                simulation.callType?.toUpperCase() !== AppConstants.CALL_TYPE_GUIDED ||
                simulation.status?.toLowerCase() === SimulationConstants.PUBLISHED.toLowerCase()
            )
      )
    );
  }

  private transformApiDataToTableRows(apiData: ISimulation[]): TableRow[] {
    return apiData
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .map((simulation) => {
        const row = this.createSimulationTableRow(
          simulation.uuid,
          simulation.name,
          simulation.moduleName,
          formatLongMonthDate(simulation.updatedAt, this.localeService.getCurrentLocaleId()),
          this.getStatusBadge(simulation),
          this.formatAssignmentCount(simulation.assignmentCount, simulation.status),
          this.getCallTypeLabel(simulation.callType),
          // SAFE: internal call-type enum comparison
          simulation.callType?.toUpperCase() === AppConstants.CALL_TYPE_GUIDED
            ? []
            : this.buildKebabMenuItems(simulation),
          simulation.moduleUuid
        );

        // Add moduleUuid to the row for direct access
        (row as any).moduleUuid = simulation.moduleUuid;
        // Store original UUID separately for easy extraction
        (row as any).simulationUuid = simulation.uuid;

        return row;
      });
  }

  private getCallTypeLabel(callType?: string): string {
    if (!callType) return this.translocoService.translate('simulationBuilder.CALL_TYPE_UNKNOWN');

    // SAFE: internal call-type enum lookup key
    const key = callType.toLowerCase() as keyof typeof CallTypeLabelMap;
    return CallTypeLabelMap[key] || this.capitalizeFirstLetter(callType);
  }

  private capitalizeFirstLetter(value: string): string {
    // SAFE: fallback capitalization of internal call-type value
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private buildKebabMenuItems(simulation: ISimulation): SimulationMenuItem[] {
    // SAFE: status enum comparison
    if (simulation.status?.toLowerCase() === 'archived') {
      return [];
    }

    const t = (key: string) => this.translocoService.translate(`simulationBuilder.${key}`);

    const items: SimulationMenuItem[] = [
      { type: 'click', action: SimulationMenuAction.Duplicate, text: t('ACTION_DUPLICATE') },
    ];

    // SAFE: status enum comparison
    if (simulation.status?.toLowerCase() !== 'published') {
      items.unshift({ type: 'click', action: SimulationMenuAction.Delete, text: t('DELETE') });
    }

    items.push({ type: 'click', action: SimulationMenuAction.Archive, text: t('MENU_ARCHIVE') });

    // SAFE: status enum comparison
    if (simulation.simulationGeneration && simulation.status?.toLowerCase() !== 'published') {
      items.push({ type: 'click', action: SimulationMenuAction.EditTags, text: t('MENU_EDIT_SIMULATION_TAGS') });
    }

    items.push({
      type: 'click',
      action: SimulationMenuAction.CopyUuid,
      text: t('MENU_COPY_UUID'),
      description: simulation.uuid,
    });

    return items;
  }

  private getStatusBadge(simulation: ISimulation): {
    kind: QaiBadgeKind;
    label: string;
  } {
    const generationBadge = this.getGenerationStatusBadge(
      simulation.simulationGeneration
    );
    if (generationBadge) {
      return generationBadge;
    }

    const status = simulation.status;
    if (!status) {
      return { kind: 'yellow', label: this.translocoService.translate('simulationBuilder.STATUS_DRAFT') };
    }

    // SAFE: status enum switch
    switch (status.toLowerCase()) {
      case 'draft':
        return { kind: 'yellow', label: this.translocoService.translate('simulationBuilder.STATUS_DRAFT') };
      case 'published':
        return { kind: 'mist-blue', label: this.translocoService.translate('simulationBuilder.STATUS_PUBLISHED') };
      case 'testing':
        return { kind: 'gray-blue', label: this.translocoService.translate('simulationBuilder.STATUS_TESTING') };
      case 'archived':
        return { kind: 'gray', label: this.translocoService.translate('simulationBuilder.STATUS_ARCHIVED') };
      default:
        return { kind: 'yellow', label: status };
    }
  }

  // Generation status takes precedence over Call.Status while a self-authored
  // simulation is still being built; once COMPLETED (or absent) the normal
  // Call.Status badge is shown instead. Every refinement status also falls
  // back to the normal Call.Status badge for now (see REFINEMENT_STATUSES).
  private getGenerationStatusBadge(
    simulationGeneration: ISimulationGeneration | null
  ): { kind: QaiBadgeKind; label: string } | null {
    const generationStatus = simulationGeneration?.status;
    if (
      !generationStatus ||
      generationStatus === SimulationGenerationStatus.Completed ||
      REFINEMENT_STATUSES.includes(generationStatus)
    ) {
      return null;
    }

    if (
      generationStatus === SimulationGenerationStatus.Failed ||
      generationStatus === SimulationGenerationStatus.TokenLimitExceeded
    ) {
      return { kind: 'red', label: SimulationConstants.FAILED };
    }

    // Every other in-progress value, shows as "Processing"
    return { kind: 'gray-blue-outline', label: SimulationConstants.PROCESSING };
  }

  createSimulationTableRow(
    id: string | number,
    name: string,
    moduleName: string,
    date: string,
    status: { kind: QaiBadgeKind; label: string },
    assignmentCount: string,
    simulationType: string,
    options: KebabMenuItem[],
    moduleUuid: string = ''
  ): TableRow {
    // Create unique ID by combining UUID and moduleUuid
    const uniqueId = moduleUuid ? `${id}_${moduleUuid}` : String(id);

    const row: any = {
      id: uniqueId,
      [this.headers[0]]: { type: 'text', value: name },
      [this.headers[1]]: { type: 'text', value: moduleName },
      [this.headers[2]]: { type: 'text', value: date },
      [this.headers[3]]: {
        type: 'badge',
        shape: 'round',
        kind: status.kind,
        label: status.label,
      },
    };

    if (
      this.selectedAccountHasAuthor &&
      this.selectedAccountHasAssignmentManager
    ) {
      row[this.headers[4]] = {
        type: 'tooltip',
        value: [],
        label: assignmentCount,
      };
      row[this.headers[5]] = {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: simulationType,
      };
    } else {
      row[this.headers[4]] = {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: simulationType,
      };
    }

    return { ...row, options } as unknown as TableRow;
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    const action = (event.action as SimulationMenuItem).action;
    if (!action) return;

    // Extract UUID from combined ID (format: 'uuid_moduleUuid' or just 'uuid')
    const simulationUuid = this.extractSimulationUuid(event.rowId, event.rowData);
    const moduleUuid = (event.rowData as any).moduleUuid || '';

    switch (action) {
      case SimulationMenuAction.Delete:
        this.deleteSimulation.emit({
          id: simulationUuid,
          rowData: event.rowData,
          moduleUuid,
        });
        break;
      case SimulationMenuAction.Duplicate:
        this.duplicateSimulation.emit({
          id: simulationUuid,
          name: (event.rowData['Name'] as TableRowSimulationNameObject).value,
          moduleUuid,
        });
        break;
      case SimulationMenuAction.Archive:
        this.archiveSimulation.emit({ id: simulationUuid, moduleUuid });
        break;
      case SimulationMenuAction.CopyUuid:
        this.clipboard.copy(simulationUuid);
        break;
      case SimulationMenuAction.EditTags:
        this.openEditTagsModal(simulationUuid);
        break;
      default:
        break;
    }
  }

  private openEditTagsModal(simulationUuid: string): void {
    this.editTagsSimulation = this.simulations.find((sim) => sim.uuid === simulationUuid);
    this.isEditTagsModalOpen = !!this.editTagsSimulation;
  }

  onEditTagsModalClosed(): void {
    this.isEditTagsModalOpen = false;
    this.editTagsSimulation = undefined;
  }

  onSimulationTagsSaved(): void {
    this.loadSimulations();
  }

  onTableRowClick(event: {
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    // Extract UUID from combined ID and moduleUuid from rowData
    const simulationUuid = this.extractSimulationUuid(event.rowId, event.rowData);
    const moduleUuid = (event.rowData as any).moduleUuid;
    this.handleEdit(simulationUuid, moduleUuid);
  }

  private handleEdit(rowId: string | number, moduleUuid?: string) {
    // Find simulation using both uuid and moduleUuid for accurate matching
    const simulation = this.simulations.find((sim) =>
      sim.uuid === String(rowId) &&
      (!moduleUuid || sim.moduleUuid === moduleUuid)
    );

    const generationStatus = simulation?.simulationGeneration?.status;
    if (
      generationStatus &&
      generationStatus !== SimulationGenerationStatus.Completed &&
      !REFINEMENT_STATUSES.includes(generationStatus)
    ) {
      this.openGenerationModal.emit({
        callUuid: simulation!.uuid,
        moduleUuid: simulation!.moduleUuid,
        name: simulation!.name,
        status: generationStatus,
      });
      return;
    }

    // Find tableRow by matching simulationUuid or by id (for backward compatibility)
    const tableRow = this.tableData.find((row) => {
      const rowSimulationUuid = (row as any).simulationUuid;
      return rowSimulationUuid === String(rowId) || row.id === rowId;
    });
    const simulationTypeBadge = tableRow?.['Simulation type'] as BadgeCell;

    const status = simulation?.status || '';
    const callType = simulationTypeBadge?.label || null;
    const finalModuleUuid = simulation?.moduleUuid || moduleUuid || '';

    const queryParams: any = {};
    if (finalModuleUuid) {
      queryParams.moduleUuid = finalModuleUuid;
    }

    if (status) {
      queryParams.status = status.toLocaleLowerCase();
    }

    this.router.navigate(['simulations', rowId], {
      relativeTo: this.route,
      queryParams,
      state: { callType },
    });
  }

  onTooltipDataRequested = ({ row }: { row: { id: string | number } }) => {
    // Extract UUID from combined ID
    const simulationId = this.extractSimulationUuid(row.id, row as TableRow);
    const moduleUuid = (row as any).moduleUuid || '';

    this.isTooltipLoading = true;

    this.assignmentsService
      .findAssignmentsForSimulation(simulationId, moduleUuid)
      .pipe(
        tap((res) =>
          console.log(
            `Fetched all assignments for simulation ${simulationId}`,
            res
          )
        ),
        finalize(() => {
          this.isTooltipLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (assignments: IAssignment[]) => {
          const assignmentNames = assignments.map((a) => a.name || 'Untitled');
          this.qaiTableRef?.setTooltipData(assignmentNames);
        },
        error: (err: any) => {
          console.error(
            `Failed to fetch assignments for simulation ${simulationId}:`,
            err
          );
          this.qaiTableRef?.setTooltipData([this.translocoService.translate('simulationBuilder.ERROR_LOADING_ASSIGNMENTS')]);
        },
      });
  };

  /**
   * Extracts the simulation UUID from a combined ID.
   * Combined ID format: 'uuid_moduleUuid' or just 'uuid' if no moduleUuid
   */
  private extractSimulationUuid(rowId: string | number, rowData: TableRow): string {
    // If simulationUuid is stored separately, use it (most reliable)
    if ((rowData as any).simulationUuid) {
      return String((rowData as any).simulationUuid);
    }

    // Otherwise, extract from combined ID
    const combinedId = String(rowId);
    // Check if it's a combined ID (contains underscore separator)
    if (combinedId.includes('_')) {
      return combinedId.split('_')[0];
    }

    // Fallback: return as-is if not combined
    return combinedId;
  }

  private formatAssignmentCount(assignmentCount: number, status: string): string {
    // SAFE: status enum comparison
    if (status.toLowerCase() !== 'published') {
      return '';
    }

    if (!assignmentCount || assignmentCount === 0) {
      return this.translocoService.translate('simulationBuilder.ASSIGNMENT_COUNT_NONE');
    }

    return this.translocoService.translate(
      assignmentCount > 1
        ? 'simulationBuilder.ASSIGNMENT_COUNT_PLURAL'
        : 'simulationBuilder.ASSIGNMENT_COUNT_SINGULAR',
      { count: assignmentCount }
    );
  }

  onStatusFilterChange(selectedStatuses: string | string[]): void {
    this.selectedStatusValues = Array.isArray(selectedStatuses) ? selectedStatuses : [selectedStatuses];
    SelfServeLocalStorageUtil.setSimulationStatusFilters(this.selectedStatusValues);
    this.applyFilters();
  }

  onSearchNameChange(event: Event): void {
    this.searchName.set((event.target as HTMLInputElement).value);
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SIMBUILDER_SEARCH_SIMULATION);
    this.applyFilters();
  }

  private applyFilters(resetPage = true): void {
    const locale = this.translocoService.getActiveLang() || 'en';
    const search = localeLowerCase(this.searchName(), locale).trim();
    // SAFE: fixed internal constant
    const archivedValue = SimulationConstants.ARCHIVED.toLowerCase();
    const statusFiltered = this.simulations.filter(sim => {
      // SAFE: status enum comparison
      const status = (sim.status ?? 'draft').toLowerCase();
      return this.selectedStatusValues.length
        ? this.selectedStatusValues.includes(status)
        : status !== archivedValue;
    });
    const filtered = filterByName(statusFiltered, search, s => s.name || '', locale);

    if (resetPage) {
      this.currentPage = 1;
      this.PaginationControllerService.setSimulationsCurrentPage(1);
    }
    this.tableData = this.transformApiDataToTableRows(filtered);
  }

  get hasNoSimulations(): boolean {
    return this.simulations.length === 0;
  }

  onPageChanged(event: { page: number }): void {
    this.currentPage = event.page;

    this.PaginationControllerService.setSimulationsCurrentPage(this.currentPage);
  }
}
